import styled from 'styled-components';
import moment from 'moment';
import { BUYER_VEHICLE_STATUS, DATE_FORMAT } from '../../../../../../constants';
import {
  useBuyerCounterControls,
  useNoSelectorVehicleOfferAuctionById,
  usePrivateOfferAmount,
  useTimerConfig
} from '../../../../../../hooks';
import { useCurrentUser } from '../../../../../../hooks/selectors';
import { OfferAuctionStatus, OfferStatus, Vehicle } from '../../../../../../types/vehicle';
import { auctionOfferStatuses } from '../../../../../../utils/statusCalculator';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { ReservePriceSection, SectionDivider } from '../../../../../Shared/VehicleDetails/components';
import { AcceptPublicOfferSection } from './AcceptPublicOfferSection';
import { AcceptOfferModal, SendOfferModal } from '../../../../../../components/Offers';
import { FormattedAmount, Timer } from '../../../../../../components';
import { ActionDivider } from '../../UI';
import { BuyNowSection } from './BuyNowSection';
import { AlternativeOfferInfoSection } from './AlternativeOfferInfoSection';

type Props = {
  vehicle: Vehicle;
};

const StyledDescription = styled(Typography)`
  && {
    color: #212121;
    line-height: 100%;
    font-size: 16px;
    text-align: center;
  }
`;

const CounterAmountWrapper = styled.div`
  color: #000;
  font-size: 16px;
  line-height: 100%;
  padding-bottom: 12px;
`;

const TimerWrapper = styled(Grid)`
  & > h2 {
    margin: 0;
  }

  & > span {
    width: unset;
  }
`;

const statusDescription = {
  [BUYER_VEHICLE_STATUS.PULLED]: 'The vehicle has been pulled and it is no longer available for bidding',
  [BUYER_VEHICLE_STATUS.UNDECIDED]: `Congratulations! You are the Top Buyer and have a solo chance to negotiate with the seller.
    You will see the updates here.`,
  [BUYER_VEHICLE_STATUS.PENDING_APPROVAL]:
    'The high bid is being reviewed. Once a counter or acceptance is processed, you will see the update here.',
  [BUYER_VEHICLE_STATUS.SELLER_ACCEPTING_OFFERS]: `The high bid is being reviewed. Once a counter or acceptance is processed, you will see the update here.`
};

const CounterControlsWrapper = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 8px;
  padding-bottom: 20px;
`;

const StyledViewReportOfSale = styled(Typography)`
  && {
    color: #005a96;
    text-decoration: underline;
    cursor: pointer;
    text-align: right;
    font-weight: 500;
    margin-top: 17px;
  }
`;

const CounterButton = styled(Button)<{ textColor?: string; color: string }>`
  border-radius: 20px;
  flex: 1 1 100%;
  height: 44px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  background: ${props => props.color};
  color: ${props => props.textColor};
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: ${props => props.color};
  }
`;

export const BuyerActiveOffers = ({ vehicle }: Props) => {
  const currentUser = useCurrentUser();
  const offerAuction = useNoSelectorVehicleOfferAuctionById(vehicle.id)!;
  const isBuyerAwarded = currentUser.id === vehicle.buyer_id;
  const amount = vehicle.vehicle_price;
  const awardedUser = vehicle.awarded_user;
  const counterControlButtons = useBuyerCounterControls(vehicle.id);
  const [, acceptButton] = counterControlButtons;
  const status = auctionOfferStatuses(vehicle, offerAuction, currentUser);
  const { timer, colorsConfig } = useTimerConfig(vehicle, offerAuction);
  const { buyerAmount, adminOrSellerAmount } = usePrivateOfferAmount(offerAuction);

  const AwardedDescriptionComponent = () => {
    if (status === BUYER_VEHICLE_STATUS.AWARDED) {
      return (
        <StyledDescription>
          {isBuyerAwarded ? 'You' : awardedUser?.dealership_name} were awarded the vehicle for the amount of{' '}
          <NumberFormat displayType="text" prefix="$" value={amount} thousandSeparator /> on{' '}
          {moment(vehicle.date_status).format(DATE_FORMAT)}
        </StyledDescription>
      );
    }

    if (BUYER_VEHICLE_STATUS.AWARDED_PENDING_CHARGE) {
      return (
        <StyledDescription>
          {isBuyerAwarded ? 'You' : awardedUser?.dealership_name} were awarded the vehicle for the amount of{' '}
          <NumberFormat displayType="text" prefix="$" value={amount} thousandSeparator /> on{' '}
          {moment(vehicle.date_status).format(DATE_FORMAT)}
        </StyledDescription>
      );
    }

    return (
      <StyledDescription>
        {isBuyerAwarded ? 'You' : awardedUser?.dealership_name} were awarded the vehicle for the amount of{' '}
        <NumberFormat displayType="text" prefix="$" value={amount} thousandSeparator /> on{' '}
        {moment(vehicle.date_status).format(DATE_FORMAT)} but your credit card was declined during fee charging.
        Please update your credit card information on your Profile.
      </StyledDescription>
    );
  };

  switch (status) {
    case BUYER_VEHICLE_STATUS.PULLED:
    case BUYER_VEHICLE_STATUS.UNDECIDED:
    case BUYER_VEHICLE_STATUS.PENDING_APPROVAL: {
      return (
        <Grid container direction="column">
          <ReservePriceSection vehicle={vehicle} includeDivider dividerSize="sm" />
          <StyledDescription>{statusDescription[status]}</StyledDescription>
        </Grid>
      );
    }
    case BUYER_VEHICLE_STATUS.SELLER_ACCEPTING_OFFERS:
    case BUYER_VEHICLE_STATUS.TOP_BUYER_SELLER_ACCEPTING_OFFER: {
      const descriptionText = (() => {
        if (
          status === BUYER_VEHICLE_STATUS.SELLER_ACCEPTING_OFFERS &&
          offerAuction?.status === OfferAuctionStatus.PRIVATE_BIDS
        ) {
          return 'Seller accepting backup offers if top buyer and seller don’t come to an agreement.';
        }

        return statusDescription[status];
      })();
      return (
        <Grid container direction="column" style={{ gap: '8px' }}>
          <ReservePriceSection vehicle={vehicle} includeDivider dividerSize="sm" />

          {descriptionText && <StyledDescription>{descriptionText}</StyledDescription>}
          <AcceptPublicOfferSection
            offerAuction={offerAuction}
            vehicleId={vehicle.id}
            vehicleTitle={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          />
        </Grid>
      );
    }
    case BUYER_VEHICLE_STATUS.BUYER_COUNTER_SENT:
    case BUYER_VEHICLE_STATUS.COUNTERED: {
      const lastOffer = offerAuction.privateOffers?.find(el => el.status === OfferStatus.SENT);
      return (
        <Grid container direction="column" style={{ gap: '8px' }}>
          {vehicle.reserve_price_set && <ReservePriceSection dividerSize="sm" vehicle={vehicle} includeDivider />}
          <TimerWrapper container justify="space-between" alignItems="center">
            {lastOffer && lastOffer.sender.role !== 'buyer' && timer && (
              <Timer withColors colorsConfig={colorsConfig} toDate={timer} />
            )}
          </TimerWrapper>
          <CounterAmountWrapper>
            {`Your countered Amount - `}
            <FormattedAmount amount={buyerAmount} />
          </CounterAmountWrapper>
          {status === BUYER_VEHICLE_STATUS.BUYER_COUNTER_SENT && (
            <StyledDescription style={{ marginBottom: '12px' }}>
              The counter was sent to seller. You will see updates here.
            </StyledDescription>
          )}
          <CounterAmountWrapper>
            {`Seller’s countered Amount - `}
            <FormattedAmount amount={adminOrSellerAmount} />
          </CounterAmountWrapper>
          {status === BUYER_VEHICLE_STATUS.COUNTERED && (
            <StyledDescription style={{ marginBottom: '12px' }}>
              The counter was set by seller. You will see updates here.
            </StyledDescription>
          )}
          {status === BUYER_VEHICLE_STATUS.COUNTERED && (
            <CounterControlsWrapper>
              {counterControlButtons.map(btn => (
                <CounterButton key={btn.title} color={btn.color as any} textColor={btn.textColor} onClick={btn.onClick}>
                  {btn.title}
                </CounterButton>
              ))}
            </CounterControlsWrapper>
          )}
          {status === BUYER_VEHICLE_STATUS.BUYER_COUNTER_SENT && (
            <CounterControlsWrapper>
              <CounterButton
                key={acceptButton.title}
                color={acceptButton.color as any}
                textColor={acceptButton.textColor}
                onClick={acceptButton.onClick}
              >
                {acceptButton.title}
              </CounterButton>
            </CounterControlsWrapper>
          )}
          <SendOfferModal
            offerAuction={offerAuction}
            maxAmount={vehicle.reserve_price}
            form={`sendVehicleOffer-${vehicle.id}`}
            vehicle={vehicle}
          />
          <AcceptOfferModal vehicleTitle={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} vehicleId={vehicle.id} />
        </Grid>
      );
    }
    // TODO: move this to the top of the switch
    case BUYER_VEHICLE_STATUS.DECLINED: {
      return (
        <Grid container direction="column">
          <StyledDescription>The car was awarded to {awardedUser?.dealership_name}.</StyledDescription>
        </Grid>
      );
    }
    case BUYER_VEHICLE_STATUS.AWARDED:
    case BUYER_VEHICLE_STATUS.AWARDED_PENDING_CHARGE:
    case BUYER_VEHICLE_STATUS.AWARDED_NOT_CHARGED:
      return (
        <Grid container direction="column">
          <AwardedDescriptionComponent />
          {vehicle.buyer_report_of_sale && (
            <StyledViewReportOfSale onClick={() => window.open(vehicle.buyer_report_of_sale)}>
              View Report of Sale
            </StyledViewReportOfSale>
          )}
        </Grid>
      );
    case BUYER_VEHICLE_STATUS.ALTERNATIVE_UNDECIDED:
    case BUYER_VEHICLE_STATUS.ACTIVE_ALTERNATIVE_AUCTION:
      const buyNowVisible =
        !vehicle.in_battle &&
        vehicle.active_alternative_offer &&
        (status === BUYER_VEHICLE_STATUS.ACTIVE_ALTERNATIVE_AUCTION ||
          vehicle.active_alternative_offer.status === 'after_time_active');

      const hasOffer = !!vehicle.my_alternative_bid_offer;

      return (
        <Grid container direction="column">
          <ReservePriceSection vehicle={vehicle} />
          {buyNowVisible && <BuyNowSection includeDivider={vehicle.reserve_price_set} vehicle={vehicle} />}
          <AlternativeOfferInfoSection vehicle={vehicle} />
        </Grid>
      );
    default:
      return null;
  }
};
